import React, { memo } from 'react';

import {
  Tooltip,
  Typography,
} from 'antd';
import TMIcon from 'tm-icons-library';

import { DownloadOutlined } from '@ant-design/icons';

import { useTheme } from '../../../../context/ThemeProvider';
import { MEDIA_CONTENT } from '../../../../shared/constants';
import { getFileExtension } from '../../../../shared/file';
import { getSpecificFileIcon } from '../../../../shared/fileUtils';
import { numFormatterInUnit } from '../../../../shared/formatter';
import { IFileAttachment } from '../../../../types/fileTypes';

interface Props {
  attachment: IFileAttachment;
  onDownload?: (attachment: IFileAttachment) => void;
  onRemove?: (attachment: IFileAttachment) => void;
  className?: string;
  /** Separator between file type and size in subtitle (default: ' - ') */
  subtitleSeparator?: string;
}

const AttachmentItem: React.FC<Props> = ({
  attachment,
  onDownload,
  onRemove,
  className,
  subtitleSeparator = ' - ',
}) => {
  const { colors: COLORS } = useTheme();

  const { url, name, size } = attachment;

  const fileType = name ? getFileExtension(name, 'doc') : '';

  const subtitle =
    fileType && size
      ? `${fileType.toUpperCase()}${subtitleSeparator}${numFormatterInUnit(size, 0, 2, 'fileSize')}`
      : fileType
        ? fileType.toUpperCase()
        : size
          ? numFormatterInUnit(size, 0, 2, 'fileSize')
          : null;

  return (
    <div key={`file-${url}`} className={`attachment-item ${className || ''}`}>
      <span className="item__icon">
        {fileType ? (
          getSpecificFileIcon(fileType)
        ) : (
          <TMIcon
            name={MEDIA_CONTENT.doc.icon}
            size={20}
            color="currentColor"
          />
        )}
      </span>

      <div className="item__content">
        <Typography.Paragraph
          className="item__content__title"
          ellipsis={{ rows: 1, tooltip: name }}>
          {name}
        </Typography.Paragraph>

        <Typography.Text type="secondary" className="item__content__subtitle">
          {subtitle}
        </Typography.Text>
      </div>

      {onDownload || onRemove ? (
        <div className="item__action">
          {onDownload ? (
            <Tooltip title="Download" mouseEnterDelay={0.5}>
              <DownloadOutlined
                className="item__action__btn"
                size={20}
                color={COLORS.ICON}
                onClick={() => onDownload(attachment)}
              />
            </Tooltip>
          ) : null}

          {onRemove ? (
            <Tooltip title="Remove" mouseEnterDelay={0.5}>
              <TMIcon
                className="item__action__btn"
                name="trash-outline"
                size={20}
                color={COLORS.ERROR}
                onClick={() => {
                  onRemove(attachment);
                }}
              />
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default memo(AttachmentItem);
